import { useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Download, ArrowRight, Check, Boxes, ShoppingBag, Wrench, Users, BarChart3, Lock } from "lucide-react";

const POS_IMG = "https://images.unsplash.com/photo-1726065235239-b20b88d43eea?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";
const DASH_IMG = "https://images.unsplash.com/photo-1649424221028-8e7d31f2e3c9?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";

const features = [
  { icon: Boxes, t: "Inventory", d: "Track IMEIs, serials, stock per branch in real-time." },
  { icon: ShoppingBag, t: "Quick checkout", d: "Sell in seconds, with receipts and barcodes." },
  { icon: Wrench, t: "Repair tracking", d: "Tickets, statuses, parts, and customer SMS updates." },
  { icon: Users, t: "Staff & permissions", d: "Manage cashiers, technicians, and admins." },
  { icon: BarChart3, t: "Reports", d: "Daily sales, top items, technician performance." },
  { icon: Lock, t: "Activation key", d: "Per-shop license, secured & traceable." },
];

const plans = [
  { name: "Single Shop", price: "£149", note: "one-time license", per: ["1 branch", "Up to 3 users", "Lifetime updates (1 year)", "Email support"] },
  { name: "Multi-shop", price: "£399", note: "one-time license", per: ["Up to 5 branches", "Unlimited users", "Lifetime updates (2 years)", "Priority support"], featured: true },
  { name: "Enterprise", price: "Custom", note: "talk to us", per: ["Unlimited branches", "Custom features", "SLA support", "On-site setup"] },
];

export default function PosSystem() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", shop: "", plan: "Single Shop", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", { type: "pos", name: form.name, email: form.email, phone: form.phone, message: form.message, extra: { shop: form.shop, plan: form.plan } });
      toast.success("Thank you! We'll contact you with your activation key shortly.");
      setForm({ name: "", email: "", phone: "", shop: "", plan: "Single Shop", message: "" });
    } catch {
      toast.error("Could not send request. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="pos-page">
      {/* HERO */}
      <section className="relative bg-[#0b1838] text-white overflow-hidden" data-testid="pos-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline text-[#aedfff] mb-4">JAS POS · for phone shops</div>
            <h1 className="font-display font-black text-5xl lg:text-7xl tracking-tighter leading-[0.95]">
              Run your shop faster.<br/>
              <span className="text-[#aedfff]">Unlimited Solutions.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl">A point-of-sale system built by phone-shop owners. Inventory, repairs, receipts, staff — all in one Windows app. Lightweight, offline-ready, and easy to learn.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={`${BACKEND_URL}/api/pos/download`} data-testid="pos-download-btn" className="inline-flex items-center gap-2 px-7 py-4 bg-[#aedfff] text-[#0b1838] font-semibold rounded-lg hover:bg-white transition-colors">
                <Download size={18} /> Download Installer
              </a>
              <a href="#request-key" data-testid="pos-request-key-cta" className="inline-flex items-center gap-2 px-7 py-4 border border-white/40 text-white font-semibold rounded-lg hover:bg-white/10 transition-colors">
                Request Activation Key <ArrowRight size={16} />
              </a>
            </div>
            <div className="mt-6 text-sm text-slate-400">Works on Windows 10/11 · 426 KB installer</div>
          </div>
          <div className="relative">
            <img src={POS_IMG} alt="POS" className="rounded-2xl shadow-2xl ring-1 ring-white/10 w-full h-[440px] object-cover" />
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="pos-features-bento">
        <div className="overline">Built for the way you sell</div>
        <h2 className="font-display font-black text-4xl mt-2 text-[#0b1838] tracking-tight">Everything a phone shop needs.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="border border-slate-200 rounded-xl p-6 bg-white hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`pos-feature-${i}`}>
                <div className="p-3 inline-flex rounded-lg bg-slate-50 border border-slate-200"><Icon className="text-[#1a2d5e]" size={22} /></div>
                <div className="mt-4 font-display font-bold text-xl text-[#0b1838]">{f.t}</div>
                <div className="text-sm text-slate-600 mt-2 leading-relaxed">{f.d}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* SCREENSHOT */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50">
          <img src={DASH_IMG} alt="POS Dashboard preview" className="w-full object-cover" />
        </div>
      </section>

      {/* PRICING + LEAD CAPTURE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12" data-testid="pos-pricing-section">
        <div>
          <div className="overline">Pricing</div>
          <h2 className="font-display font-black text-4xl mt-2 text-[#0b1838]">One-time license. No subscription.</h2>
          <p className="text-slate-600 mt-3">Buy once, run forever. After payment, we send your activation key by email or WhatsApp.</p>
          <div className="mt-8 space-y-4">
            {plans.map((p) => (
              <div key={p.name} className={`p-6 border rounded-xl ${p.featured ? "border-[#1a2d5e] bg-[#0b1838] text-white" : "border-slate-200 bg-white"}`} data-testid={`pos-plan-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-end justify-between">
                  <div>
                    <div className={`overline ${p.featured ? "text-[#aedfff]" : ""}`}>{p.name}</div>
                    <div className="font-display font-black text-3xl mt-1">{p.price}</div>
                    <div className={`text-xs ${p.featured ? "text-slate-300" : "text-slate-500"}`}>{p.note}</div>
                  </div>
                  {p.featured && <span className="bg-[#aedfff] text-[#0b1838] text-xs font-semibold px-2 py-1 rounded">Best value</span>}
                </div>
                <ul className="mt-4 space-y-1 text-sm">
                  {p.per.map((x, i) => <li key={i} className="flex items-center gap-2"><Check size={14} className={p.featured ? "text-[#aedfff]" : "text-[#1a2d5e]"} /> {x}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <form id="request-key" onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-8 h-fit lg:sticky lg:top-28" data-testid="pos-lead-form">
          <div className="overline">Request your activation key</div>
          <h3 className="font-display font-black text-2xl mt-2 text-[#0b1838]">We'll contact you within 24 hours</h3>
          <div className="mt-6 grid gap-4">
            <input required placeholder="Your name" data-testid="pos-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />
            <input required placeholder="Shop name" data-testid="pos-form-shop" value={form.shop} onChange={(e) => setForm({ ...form, shop: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />
            <div className="grid sm:grid-cols-2 gap-3">
              <input required type="email" placeholder="Email" data-testid="pos-form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />
              <input required placeholder="Phone / WhatsApp" data-testid="pos-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />
            </div>
            <select data-testid="pos-form-plan" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg bg-white">
              {plans.map((p) => <option key={p.name}>{p.name}</option>)}
            </select>
            <textarea rows={3} placeholder="Anything we should know? (branches, custom needs…)" data-testid="pos-form-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />
            <button type="submit" disabled={submitting} data-testid="pos-form-submit" className="px-6 py-4 bg-[#1a2d5e] text-white font-semibold rounded-lg hover:bg-[#0b1838] transition-colors disabled:opacity-60">{submitting ? "Sending…" : "Request Activation Key"}</button>
            <a href={`${BACKEND_URL}/api/pos/download`} data-testid="pos-form-download" className="text-center text-sm text-[#1a2d5e] font-semibold hover:underline">…or download the installer first</a>
          </div>
        </form>
      </section>
    </div>
  );
}
